import React, { useState } from "react";
import { toast } from "sonner";
import { Field, Input, Select, Button } from "@/components/ui-kit";
import { formatIDR, parseIDR } from "@/lib/format";

/**
 * Price Adjustment Field
 * Renders a single price field (label + current value input) AND 3 helper
 * columns below it:
 *   1. Direction: "" | "naik" | "turun" | "tetap"
 *   2. Mode: "" | "nominal" | "persen"     (disabled until col 1 filled and not "tetap")
 *   3. Value input                          (disabled until col 2 filled)
 * Plus a "Terapkan" button that computes the new price and pushes it to
 * `onChange` (parent form state). The 3 helper inputs persist locally so the
 * user can preview the math before applying.
 */
export function PriceAdjustmentField({ label, value, onChange, testId, disabled = false }) {
  const [direction, setDirection] = useState("");
  const [mode, setMode] = useState("");
  const [delta, setDelta] = useState("");

  const oldVal = parseIDR(value) || 0;
  const deltaNum = parseFloat(String(delta || "0").replace(",", ".")) || 0;

  let preview = oldVal;
  if (direction === "tetap") preview = oldVal;
  else if (direction === "turun" && mode === "nominal") preview = oldVal - deltaNum;
  else if (direction === "turun" && mode === "persen") preview = oldVal - (oldVal * deltaNum) / 100;
  else if (direction === "naik" && mode === "nominal") preview = oldVal + deltaNum;
  else if (direction === "naik" && mode === "persen") preview = oldVal + (oldVal * deltaNum) / 100;

  const previewActive = direction === "tetap" || (direction && mode && delta !== "");
  const diff = preview - oldVal;

  const apply = () => {
    if (!direction) {
      toast.error("Pilih arah perubahan dulu");
      return;
    }
    if (direction !== "tetap" && (!mode || delta === "")) {
      toast.error("Lengkapi mode dan nilai");
      return;
    }
    const next = Math.max(0, Math.round(preview * 100) / 100);
    onChange(next);
    toast.success(`${label} diperbarui: ${formatIDR(next)}`);
    // Reset helpers so the next apply starts fresh
    setDirection("");
    setMode("");
    setDelta("");
  };

  const reset = () => {
    setDirection("");
    setMode("");
    setDelta("");
  };

  const col2Disabled = disabled || !direction || direction === "tetap";
  const col3Disabled = disabled || col2Disabled || !mode;

  return (
    <div className={`md:col-span-2 border border-stone-200 p-3 space-y-2 bg-stone-50/30 ${disabled ? "opacity-70" : ""}`}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
        <Field label={label}>
          <Input
            className="font-mono-num"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            title={disabled ? "Butuh hak akses price.edit_delivery_cost" : ""}
            data-testid={`${testId}-value`}
          />
        </Field>
        {previewActive && (
          <div className="text-xs space-y-0.5 px-2 py-1 bg-stone-100 border border-stone-200">
            <div className="text-stone-500 uppercase tracking-wider text-[10px]">Preview Harga Baru</div>
            <div className="font-mono-num font-semibold text-stone-900">{formatIDR(preview)}</div>
            <div className={`text-[10px] font-mono ${diff > 0 ? "text-emerald-700" : diff < 0 ? "text-red-700" : "text-stone-500"}`}>
              {diff > 0 ? "+" : ""}{formatIDR(diff)} dari sebelumnya
            </div>
          </div>
        )}
      </div>
      <div className="grid grid-cols-12 gap-2 items-end">
        <div className="col-span-12 md:col-span-4">
          <label className="block text-[10px] uppercase tracking-wider font-semibold text-stone-500 mb-1">Arah Perubahan</label>
          <Select
            value={direction}
            onChange={(e) => { setDirection(e.target.value); if (e.target.value === "tetap") { setMode(""); setDelta(""); } }}
            disabled={disabled}
            data-testid={`${testId}-direction`}
          >
            <option value="">— Pilih —</option>
            <option value="turun">Harga Turun</option>
            <option value="naik">Harga Naik</option>
            <option value="tetap">Harga Tetap</option>
          </Select>
        </div>
        <div className="col-span-6 md:col-span-3">
          <label className="block text-[10px] uppercase tracking-wider font-semibold text-stone-500 mb-1">Mode</label>
          <Select
            value={mode}
            onChange={(e) => setMode(e.target.value)}
            disabled={col2Disabled}
            data-testid={`${testId}-mode`}
          >
            <option value="">— Pilih —</option>
            <option value="nominal">Nominal</option>
            <option value="persen">Persentase</option>
          </Select>
        </div>
        <div className="col-span-6 md:col-span-3">
          <label className="block text-[10px] uppercase tracking-wider font-semibold text-stone-500 mb-1">
            {mode === "persen" ? "Nilai (%)" : "Nominal (Rp)"}
          </label>
          <Input
            className="font-mono-num"
            value={delta}
            onChange={(e) => setDelta(e.target.value)}
            disabled={col3Disabled}
            placeholder={mode === "persen" ? "0" : "0"}
            data-testid={`${testId}-delta`}
          />
        </div>
        <div className="col-span-12 md:col-span-2 flex gap-1">
          <Button onClick={apply} disabled={disabled} className="!py-2 !px-3 text-xs w-full" data-testid={`${testId}-apply`}>Terapkan</Button>
          {previewActive && (
            <Button variant="ghost" onClick={reset} className="!py-2 !px-2 text-xs" data-testid={`${testId}-reset`}>×</Button>
          )}
        </div>
      </div>
    </div>
  );
}
